// Notification_Frequency_Modal.jsx
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Modal, Radio } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Back from "@/components/specific/Back";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import SuccessAlert from "@/components/common/alerts/Success";
import ErrorAlert from "@/components/common/alerts/Error";
import {
  updateProfileAsync,
  selectIsUpdating,
  selectNotificationPreferences,
} from "@/store/redux/slices/user/user_slice";

const FREQUENCY_OPTIONS = [
  {
    id: "realtime",
    title: "Real-time",
    description: "Get notified immediately",
    icon: "LongArrowUp",
  },
  {
    id: "daily",
    title: "Daily Digest",
    description: "Get notifications once a day",
    icon: "LongTwoWayArrow",
  },
  {
    id: "weekly",
    title: "Weekly Summary",
    description: "Get notifications once a week",
    icon: "LongArrowDown",
  },
];

const NotificationFrequencyModal = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const notificationPreferences = useSelector(selectNotificationPreferences);
  const isUpdating = useSelector(selectIsUpdating);

  // Seed selected frequency from Redux; fall back to "realtime" if not yet loaded.
  const [selectedFrequency, setSelectedFrequency] = useState("realtime");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  
  useEffect(() => {
    if (isVisible && notificationPreferences?.frequency) {
      setSelectedFrequency(notificationPreferences.frequency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleSave = async () => {
    try {
      // eslint-disable-next-line no-unused-vars
      const { _id: _prefId, ...prefsWithoutId } = notificationPreferences || {};

      await dispatch(
        updateProfileAsync({
          notificationPreferences: {
            ...prefsWithoutId,
            frequency: selectedFrequency,
          },
        })
      ).unwrap();

      setShowSuccess(true);
    } catch (err) {
      setErrorMessage(
        typeof err === "string" ? err : "Failed to update notification frequency."
      );
      setShowError(true);
    }
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center gap-4">
              <Back removeText={true} color="black" onClick={onClose} />
              <h2 className="text-bold-x text-primary">Notification Frequency</h2>
            </div>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="space-y-4">
              <Radio.Group
                value={selectedFrequency}
                onChange={(e) => setSelectedFrequency(e.target.value)}
                className="w-full"
                style={{
                  "--ant-primary-color": "var(--veegil-color-primary)",
                }}>
                {FREQUENCY_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg cursor-pointer transition-all duration-300">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <WebIcon icon={option.icon} className="w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="text-bold text-black">{option.title}</h3>
                        <p className="text-small dark-mode-text">
                          {option.description}
                        </p>
                      </div>
                    </div>
                    <Radio value={option.id} className="custom-radio" />
                  </div>
                ))}
              </Radio.Group>
            </div>
          </div>

          {/* Footer */}
          <div className="w-full bg-secondary p-4">
            <Button
              text={isUpdating ? "Saving…" : "Save Changes"}
              onClick={handleSave}
              isActive={!isUpdating}
              disabled={isUpdating}
              color="primary"
              className="w-full"
              customTextColor="white"
            />
          </div>
        </div>
      </Modal>

      {/* Success feedback modal */}
      <SuccessAlert
        isVisible={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          onClose();
        }}
        header="Frequency Updated"
        paragraph="Your notification frequency preference has been saved."
        buttonText="Done"
        onButtonClick={() => {
          setShowSuccess(false);
          onClose();
        }}
      />

      {/* Error feedback modal */}
      <ErrorAlert
        isVisible={showError}
        onClose={() => setShowError(false)}
        header="Update Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
        handleRecovery={() => setShowError(false)}
      />
    </>
  );
};

NotificationFrequencyModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default NotificationFrequencyModal;
